/* eslint-disable */
// @ts-nocheck

import React, { useEffect, useMemo, useState } from 'react'

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'

import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PurchaseOrder } from '@/interfaces/purchaseOrder'

import { HeaderActions } from './HeaderActions'
import { Navigation } from './Navigation'
import { useTableContext } from './TableContext'

interface DataTableProps {
  columns: ColumnDef<PurchaseOrder>[]
  _showEditPurchaseOrder: boolean
  _showDeletePurchaseOrder: boolean
  _onClose: () => void
  _onCloseDelete: () => void
  _purchaseOrderToEdit?: PurchaseOrder
  _purchaseOrderToDelete?: string
  onNewPurchaseOrder: () => void
}

const PURCHASE_ORDER_TABLE_COLUMNS_STORAGE_KEY = 'purchase-order-table-columns'

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  _showEditPurchaseOrder,
  _showDeletePurchaseOrder,
  _onClose,
  _onCloseDelete,
  _purchaseOrderToEdit,
  _purchaseOrderToDelete,
  onNewPurchaseOrder,
}) => {
  const { purchaseOrderData, isLoading } = useTableContext()

  // Column visibility state with localStorage persistence
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(
        PURCHASE_ORDER_TABLE_COLUMNS_STORAGE_KEY
      )
      return saved ? JSON.parse(saved) : {}
    }
  )

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(
      PURCHASE_ORDER_TABLE_COLUMNS_STORAGE_KEY,
      JSON.stringify(columnVisibility)
    )
  }, [columnVisibility])

  const data = useMemo(() => {
    return purchaseOrderData?.data || []
  }, [purchaseOrderData?.data])

  const table = useReactTable({
    data: data as PurchaseOrder[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      columnVisibility,
    },
  })

  return (
    <div className="w-full">
      <div className="flex flex-col rounded-md border bg-white">
        <HeaderActions
          columns={table.getAllColumns()}
          onNewPurchaseOrder={onNewPurchaseOrder}
        />
        <div className="relative flex-1 overflow-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead className="whitespace-nowrap" key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    {table.getAllColumns().map((column) => (
                      <TableCell className="py-3" key={column.id}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    className="hover:bg-muted/50"
                    data-state={row.getIsSelected() && 'selected'}
                    key={row.id}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell className="py-3" key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    className="h-24 text-center"
                    colSpan={columns.length}
                  >
                    No se encontraron órdenes de compra.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <Navigation />
      </div>
    </div>
  )
}
